import { poLineSpecColor, receiptPoLineSpecColor } from '@/features/purchasing/specColor'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { ExcelGrid } from '@/components/excelGrid/ExcelGrid'
import { type ExcelColumnDef, getColHeader, getColVal, getColKey } from '@/components/excelGrid/types'
import { exportToCsv } from '@/components/excelGrid/tsvUtils'
import {
  listReceipts,
  patchReceipt,
  voidReceipt,
  getReceiptHistory,
  receivePurchaseOrder,
  recordLineShort,
  listPurchaseOrders,
  getPurchaseOrder,
} from '@/services/procurement/apiClient'
import type { PurchaseOrderDto, ReceiptDto, DocumentChangeLogDto } from '@/services/procurement/types'
import {
  getStockOverview,
  listStockInbounds,
  createStockInbound,
  patchStockInbound,
  voidStockInbound,
  getStockInboundHistory,
  recordStockInboundPrintLog,
  recordReceiptPrintLog,
} from '@/services/inventory/apiClient'
import type { MaterialStockOverviewItem, StockInboundDto } from '@/services/inventory/types'
import { receiveFreePo } from '@/services/procurement/freePoApi'
import { getGroupDef, formatMaterialAttributes } from '@/domain/materialGroups'
import { useCan, useAuth } from '@/features/auth/AuthContext'
import { formatDateFull, formatDateTimeFull } from '@/lib/dateFormat'
import { Printer } from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  MaterialTypeahead,
  matchMaterialFromExcelText,
} from '@/components/forms/MaterialTypeahead'
import {
  WarehouseDocumentPrintModal,
  type PrintDocumentData,
  type PrintDocumentLine,
} from './WarehouseDocumentPrintModal'

function fmtN(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
}

type UnifiedReceiptRow = {
  id: string
  code: string
  kind: 'PO' | 'DIRECT'
  poId?: string
  sourceType: string
  ref: string
  supplierName: string
  materialNames: string
  lineCount: number
  at: string
  by: string
  delivererName?: string
  editCount: number
  voidReason: string
  note: string
  status: string
  isVoid: boolean
  lines: Array<{
    id: string
    materialId: string
    materialName: string
    poLineId?: string
    spec?: string
    color?: string
    groupCode?: string | null
    uom: string
    qty: number
    note?: string | null
  }>
}

type ShortResOption = 'Chờ giao thêm' | 'Chấp nhận thiếu' | 'Hủy phần còn lại' | 'Khiếu nại NCC'

export function ReceiptsPage() {
  const { user } = useAuth()
  const canInventory = useCan('inventory.write')
  const canReceiving = useCan('receiving.write')
  const canWriteInventory = canInventory || canReceiving || (user?.roles ? user.roles.some((r) => r === 'WAREHOUSE') : false)

  const [activeSubTab, setActiveSubTab] = useState<'po' | 'doc'>('po')
  const [searchQuery, setSearchQuery] = useState('')
  const [resetWidthsCounter, setResetWidthsCounter] = useState(0)
  const [loading, setLoading] = useState(true)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const [pos, setPos] = useState<PurchaseOrderDto[]>([])
  const [receipts, setReceipts] = useState<ReceiptDto[]>([])
  const [inbounds, setInbounds] = useState<StockInboundDto[]>([])
  const [materials, setMaterials] = useState<MaterialStockOverviewItem[]>([])

  // Modal states
  const [receiveModalPo, setReceiveModalPo] = useState<PurchaseOrderDto | null>(null)
  const [receiveLinesState, setReceiveLinesState] = useState<
    Array<{
      lineId: string
      materialId: string
      qtyToReceive: number | string
      lineNote: string
      shortAction: ShortResOption | ''
    }>
  >([])
  const [receiveNote, setReceiveNote] = useState('')

  const [detailDoc, setDetailDoc] = useState<UnifiedReceiptRow | null>(null)
  const [isEditingDoc, setIsEditingDoc] = useState(false)
  const [editLineQtys, setEditLineQtys] = useState<Record<string, number>>({})
  const [editLineNotes, setEditLineNotes] = useState<Record<string, string>>({})
  const [editReason, setEditReason] = useState('')
  const [docHistory, setDocHistory] = useState<DocumentChangeLogDto[]>([])

  const [directModalOpen, setDirectModalOpen] = useState(false)
  const [directSource, setDirectSource] = useState<'OPENING' | 'MISC_PURCHASE' | 'OUTSIDE_PO' | 'OTHER'>('MISC_PURCHASE')
  const [directDelivererName, setDirectDelivererName] = useState('')
  const [directNote, setDirectNote] = useState('')
  const [receiveDelivererName, setReceiveDelivererName] = useState('')
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [printDocData, setPrintDocData] = useState<PrintDocumentData | null>(null)
  const [printDocMeta, setPrintDocMeta] = useState<{ id: string; kind: 'PO' | 'DIRECT' } | null>(null)
  const [directLines, setDirectLines] = useState<Array<{ materialId: string; qty: number | string; note: string; error?: string }>>([
    { materialId: '', qty: '', note: '' },
    { materialId: '', qty: '', note: '' },
    { materialId: '', qty: '', note: '' },
  ])

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => setToastMessage(null), 3500)
  }, [])

  const handlePasteDirectLines = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain')
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()

    const rows = text.split(/\r?\n/).filter((r) => r.trim())
    const parsedRows: Array<{ materialId: string; qty: number | string; note: string; error?: string }> = []

    for (const r of rows) {
      const cols = r.split('\t')
      const rawMatName = cols[0]?.trim() || ''
      const rawQty = cols[1]?.trim() || ''
      const rawNote = cols[2]?.trim() || ''

      if (!rawMatName) continue

      const matchRes = matchMaterialFromExcelText(rawMatName, materials)
      let matId = ''
      let err: string | undefined = undefined

      if (matchRes.material) {
        matId = matchRes.material.material_id
      } else if (matchRes.ambiguous) {
        err = 'Chưa chọn thông số — bấm để chọn'
      } else {
        err = `Không khớp: "${rawMatName}"`
      }

      parsedRows.push({
        materialId: matId,
        qty: rawQty && !isNaN(Number(rawQty)) ? Number(rawQty) : '',
        note: rawNote,
        error: err,
      })
    }

    if (parsedRows.length > 0) {
      setDirectLines((prev) => {
        const existing = prev.filter((l) => l.materialId || l.qty)
        return [...existing, ...parsedRows]
      })
      showToast(`Đã dán ${parsedRows.length} dòng từ Excel`)
    }
  }, [materials, showToast])

  const loadAllData = useCallback(async () => {
    setLoading(true)
    try {
      const [poList, recList, inbList, matList] = await Promise.all([
        listPurchaseOrders().catch(() => []),
        listReceipts().catch(() => []),
        listStockInbounds().catch(() => []),
        getStockOverview().catch(() => []),
      ])
      setPos(poList)
      setReceipts(recList)
      setInbounds(inbList)
      setMaterials(matList)
    } catch {
      showToast('Lỗi khi tải dữ liệu phiếu nhập')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void loadAllData()
  }, [loadAllData])

  const materialMap = useMemo(() => {
    const map = new Map<string, MaterialStockOverviewItem>()
    for (const m of materials) {
      map.set(m.material_id, m)
    }
    return map
  }, [materials])

  // PO lines helper
  const isPoLineClosed = useCallback((l: any) => {
    const o = Number(l.order_qty || l.order_qty_base || 0)
    const r = Number(l.received_qty || 0)
    const s = Number(l.short_qty || 0)
    return r >= o || (r + s >= o && s > 0)
  }, [])

  const poStatus = useCallback((p: PurchaseOrderDto) => {
    const lines = p.lines || []
    if (lines.length === 0) return 'Chờ nhận'
    const allClosed = lines.every((l) => isPoLineClosed(l))
    const anyReceived = lines.some((l) => Number(l.received_qty || 0) > 0)
    if (allClosed) {
      const anyShort = lines.some((l) => Number(l.received_qty || 0) < Number(l.order_qty || 0))
      return anyShort ? 'Đóng (thiếu)' : 'Đã nhận đủ'
    }
    return anyReceived ? 'Giao một phần' : 'Chờ nhận'
  }, [isPoLineClosed])

  // Flattened Unified Receipts
  const unifiedReceiptRows: UnifiedReceiptRow[] = useMemo(() => {
    const out: UnifiedReceiptRow[] = []
    const poMap = new Map<string, PurchaseOrderDto>()
    for (const p of pos) {
      poMap.set(p.id, p)
    }

    // 1. PO Receipts
    for (const r of receipts) {
      const lines = (r.lines || []).map((l: any) => {
        const poLine = poMap.get(r.po_id)?.lines?.find(pl => pl.id === l.po_line_id)
        const materialId = l.material_id || poLine?.material_id
        const mat = materialMap.get(materialId)
        const captured = poLineSpecColor(poLine ?? {}, mat?.attributes)
        return {
          id: l.id,
          materialId,
          poLineId: l.po_line_id,
          spec: captured.spec,
          color: captured.color,
          materialName: mat?.name || mat?.material_name || l.material_name || 'Vật tư',
          groupCode: mat?.group_code,
          uom: mat?.base_uom || mat?.uom_default || l.base_uom || '',
          qty: Number(l.qty_order_uom || l.qty_base || l.received_qty || 0),
          note: l.note || '',
        }
      })
      out.push({
        id: r.id,
        code: r.code,
        kind: 'PO',
        poId: r.po_id,
        sourceType: 'PO',
        ref: r.po_code || (r as any).ref || '—',
        supplierName: (r as any).supplier_name || poMap.get(r.po_id)?.supplier_name || '—',
        materialNames: lines.map((x) => x.materialName).join(', '),
        lineCount: lines.length,
        at: r.created_at || (r as any).received_at || '',
        by: r.received_by || (r as any).created_by || 'Kho',
        delivererName: r.deliverer_name || (r as any).deliverer_name || '',
        editCount: (r as any).edit_count || 0,
        voidReason: (r as any).void_reason || '',
        note: r.notes || (r as any).note || '',
        status: r.is_void ? 'Đã hủy' : 'Hoạt động',
        isVoid: Boolean(r.is_void),
        lines,
      })
    }

    // 2. Direct Inbounds
    for (const inb of inbounds) {
      const srcText =
        inb.source === 'OPENING'
          ? 'Đầu kỳ'
          : inb.source === 'MISC_PURCHASE'
            ? 'Mua lẻ'
            : inb.source === 'OUTSIDE_PO'
              ? 'Ngoài PO'
              : 'Khác'
      const lines = (inb.lines || []).map((l: any) => {
        const mat = materialMap.get(l.material_id)
        return {
          id: l.id,
          materialId: l.material_id,
          materialName: mat?.material_name || 'Vật tư',
          groupCode: mat?.group_code,
          uom: mat?.base_uom || l.base_uom || '',
          qty: Number(l.qty_base || 0),
          note: l.note || '',
        }
      })
      out.push({
        id: inb.id,
        code: inb.code,
        kind: 'DIRECT',
        sourceType: 'Trực tiếp',
        ref: srcText,
        supplierName: '—',
        materialNames: lines.map((x) => x.materialName).join(', '),
        lineCount: lines.length,
        at: inb.created_at || '',
        by: inb.created_by || 'Kho',
        delivererName: inb.deliverer_name || '',
        editCount: 0,
        voidReason: inb.void_reason || '',
        note: inb.note || '',
        status: inb.status === 'VOID' ? 'Đã hủy' : 'Hoạt động',
        isVoid: inb.status === 'VOID',
        lines,
      })
    }

    // Sort descending by date
    out.sort((a, b) => (b.at || '').localeCompare(a.at || ''))
    return out
  }, [receipts, inbounds, materialMap])

  // Count badges
  const pendingPoCount = useMemo(() => {
    return pos.filter((p) => {
      if (!['APPROVED', 'SENT', 'PARTIAL'].includes(p.status)) return false
      const st = poStatus(p)
      return st === 'Chờ nhận' || st === 'Giao một phần'
    }).length
  }, [pos, poStatus])

  const receivablePos = useMemo(
    () => pos.filter((p) => ['APPROVED', 'SENT', 'PARTIAL'].includes(p.status)),
    [pos],
  )

  // Columns for PO tab
  const poCols: ExcelColumnDef<PurchaseOrderDto>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Mã PO',
      width: 140,
      isLink: true,
      tooltip: () => 'Bấm để nhận hàng cho PO này',
      getValue: (p) => p.code,
    },
    {
      key: 'supplier',
      header: 'Nhà cung cấp',
      width: 170,
      getValue: (p) => p.supplier_name || '—',
    },
    {
      key: 'group',
      header: 'Nhóm hàng',
      width: 140,
      getValue: (p) => {
        const firstLineMatId = p.lines?.[0]?.material_id
        const mat = firstLineMatId ? materialMap.get(firstLineMatId) : null
        return getGroupDef(mat?.group_code).name
      },
    },
    {
      key: 'created_at',
      header: 'Ngày đặt',
      width: 100,
      align: 'center',
      getValue: (p) => formatDateFull(p.created_at || (p as any).order_date),
      renderCell: (p) => {
        const val = p.created_at || (p as any).order_date
        return val ? <span title={formatDateFull(val)}>{formatDateFull(val)}</span> : <span>—</span>
      },
    },
    {
      key: 'created_by',
      header: 'Người đặt',
      width: 130,
      getValue: (p) => p.created_by || '—',
      tooltip: (p) => p.created_by || undefined,
    },
    {
      key: 'total_ordered',
      header: 'Tổng đặt',
      width: 90,
      isNumeric: true,
      getValue: (p) => (p.lines || []).reduce((sum, l) => sum + Number(l.order_qty || 0), 0),
      renderCell: (p) => fmtN((p.lines || []).reduce((sum, l) => sum + Number(l.order_qty || 0), 0)),
    },
    {
      key: 'total_received',
      header: 'Đã nhận',
      width: 90,
      isNumeric: true,
      getValue: (p) => (p.lines || []).reduce((sum, l) => sum + Number(l.received_qty || 0), 0),
      renderCell: (p) => fmtN((p.lines || []).reduce((sum, l) => sum + Number(l.received_qty || 0), 0)),
    },
    {
      key: 'remaining_lines',
      header: 'Còn thiếu',
      width: 90,
      isNumeric: true,
      getValue: (p) => (p.lines || []).reduce((sum, l) => {
        const remaining = Number(l.order_qty || 0) - Number(l.received_qty || 0) - Number(l.short_qty || 0)
        return sum + Math.max(remaining, 0)
      }, 0),
      renderCell: (p) => {
        const qty = (p.lines || []).reduce((sum, l) => {
          const remaining = Number(l.order_qty || 0) - Number(l.received_qty || 0) - Number(l.short_qty || 0)
          return sum + Math.max(remaining, 0)
        }, 0)
        return qty > 0 ? <b style={{ color: '#b00020' }}>{fmtN(qty)}</b> : <span>0</span>
      },
    },
    {
      key: 'progress_pct',
      header: 'Tiến độ nhận',
      width: 100,
      isNumeric: true,
      getValue: (p) => {
        const ord = (p.lines || []).reduce((sum, l) => sum + Number(l.order_qty || 0), 0)
        const rec = (p.lines || []).reduce((sum, l) => sum + Number(l.received_qty || 0), 0)
        return ord > 0 ? Math.round((rec / ord) * 100) : 0
      },
      renderCell: (p) => {
        const ord = (p.lines || []).reduce((sum, l) => sum + Number(l.order_qty || 0), 0)
        const rec = (p.lines || []).reduce((sum, l) => sum + Number(l.received_qty || 0), 0)
        const pct = ord > 0 ? Math.round((rec / ord) * 100) : 0
        return <span>{pct}%</span>
      },
    },
    {
      key: 'eta',
      header: 'ETA',
      width: 90,
      align: 'center',
      getValue: (p) => formatDateFull((p as any).expected_delivery_date || (p as any).eta || ''),
      renderCell: (p) => {
        const val = (p as any).expected_delivery_date || (p as any).eta || ''
        return val ? <span title={formatDateFull(val)}>{formatDateFull(val)}</span> : <span>—</span>
      },
    },
    {
      key: 'status',
      header: 'Trạng thái',
      width: 120,
      getValue: (p) => poStatus(p),
      renderCell: (p) => {
        const st = poStatus(p)
        const color = st === 'Chờ nhận' ? '#333' : st === 'Giao một phần' ? '#a35a00' : '#1b6e2d'
        return <span style={{ color, fontWeight: st === 'Chờ nhận' ? 400 : 700 }}>{st}</span>
      },
    },
  ], [materialMap, isPoLineClosed, poStatus])

  // Handle open print receipt
  const handleOpenPrintReceipt = useCallback(async (r: UnifiedReceiptRow) => {
    let sourcePo: PurchaseOrderDto | null = null
    if (r.kind === 'PO' && r.poId) {
      try { sourcePo = await getPurchaseOrder(r.poId) }
      catch { showToast('Không thể tải quy cách đã chụp của PO để in'); return }
    }
    const lines: PrintDocumentLine[] = r.lines.map((l) => {
      const mat = materialMap.get(l.materialId)
      const sc = sourcePo?.lines
        ? receiptPoLineSpecColor(sourcePo.lines, l.poLineId, mat?.attributes)
        : poLineSpecColor({ spec: l.spec, spec_attributes: { color: l.color } }, mat?.attributes)
      const attrs = r.kind === 'PO'
        ? [sc.spec, sc.color].filter(Boolean).join(' · ')
        : mat ? formatMaterialAttributes(mat.group_code, mat.attributes) : ''
      return {
        material_name: l.materialName,
        attributes_desc: attrs,
        uom: l.uom,
        qty: l.qty,
        note: l.note || undefined,
      }
    })

    setPrintDocData({
      type: r.kind === 'PO' ? 'RECEIPT' : 'INBOUND',
      code: r.code,
      date: r.at,
      created_by: r.by,
      status: r.isVoid ? 'VOID' : 'ACTIVE',
      void_reason: r.voidReason,
      edit_count: r.editCount,
      deliverer_name: r.delivererName,
      supplier_name: r.supplierName !== '—' ? r.supplierName : undefined,
      po_code: r.kind === 'PO' ? r.ref : undefined,
      source_desc: r.sourceType,
      note: r.note,
      lines,
    })
    setPrintDocMeta({ id: r.id, kind: r.kind })
    setPrintModalOpen(true)
  }, [materialMap, showToast])

  // Columns for Receipts list tab
  const recCols: ExcelColumnDef<UnifiedReceiptRow>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Mã phiếu',
      width: 140,
      isLink: true,
      tooltip: () => 'Bấm để xem / sửa phiếu',
      getValue: (r) => r.code,
    },
    {
      key: 'sourceType',
      header: 'Nguồn',
      width: 90,
      getValue: (r) => r.sourceType,
    },
    {
      key: 'ref',
      header: 'PO / Lý do nhập',
      width: 150,
      getValue: (r) => r.ref,
    },
    {
      key: 'supplierName',
      header: 'Nhà cung cấp',
      width: 160,
      getValue: (r) => r.supplierName,
    },
    {
      key: 'materialNames',
      header: 'Vật tư',
      width: 250,
      getValue: (r) => r.materialNames,
    },
    {
      key: 'lineCount',
      header: 'Số dòng',
      width: 70,
      isNumeric: true,
      getValue: (r) => r.lineCount,
    },
    {
      key: 'delivererName',
      header: 'Người giao',
      width: 130,
      getValue: (r) => r.delivererName || '—',
      tooltip: (r) => r.delivererName || undefined,
    },
    {
      key: 'at',
      header: 'Ngày giờ',
      width: 110,
      align: 'center',
      getValue: (r) => formatDateTimeFull(r.at),
      renderCell: (r) => <span title={formatDateTimeFull(r.at)}>{formatDateTimeFull(r.at)}</span>,
    },
    {
      key: 'by',
      header: 'Người nhận',
      width: 130,
      getValue: (r) => r.by,
    },
    {
      key: 'status',
      header: 'Trạng thái',
      width: 100,
      getValue: (r) => r.status,
      renderCell: (r) => (
        <span style={{ color: r.isVoid ? '#999' : '#1b6e2d', fontWeight: 600 }}>
          {r.status}
        </span>
      ),
    },
    {
      key: 'editCount',
      header: 'Số lần sửa',
      width: 80,
      isNumeric: true,
      getValue: (r) => r.editCount,
    },
    {
      key: 'voidReason',
      header: 'Lý do hủy',
      width: 180,
      getValue: (r) => r.voidReason,
    },
    {
      key: 'note',
      header: 'Ghi chú phiếu',
      width: 180,
      getValue: (r) => r.note,
    },
    {
      key: 'actions',
      header: 'In',
      width: 60,
      align: 'center',
      getValue: () => '',
      renderCell: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleOpenPrintReceipt(r)
          }}
          title="In phiếu nhập kho"
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: 3,
            padding: '2px 6px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          <Printer size={14} aria-hidden="true" />
        </button>
      ),
    },
  ], [handleOpenPrintReceipt])

  // Handle opening receive modal
  const handleOpenReceive = useCallback((p: PurchaseOrderDto) => {
    if (!canWriteInventory) {
      showToast('Chỉ vai trò Kho nhận hàng. PO do Mua hàng chỉnh.')
    }
    const lines = p.lines || []
    setReceiveModalPo(p)
    setReceiveNote('')
    setReceiveLinesState(
      lines.map((l) => ({
        lineId: l.id,
        materialId: l.material_id,
        qtyToReceive: '',
        lineNote: '',
        shortAction: '',
      })),
    )
  }, [canWriteInventory, showToast])

  // Handle opening receipt detail
  const handleOpenDocModal = useCallback(async (r: UnifiedReceiptRow) => {
    setDetailDoc(r)
    setIsEditingDoc(false)
    setEditReason('')
    const initQtys: Record<string, number> = {}
    const initNotes: Record<string, string> = {}
    r.lines.forEach((l) => {
      initQtys[l.id] = l.qty
      initNotes[l.id] = l.note || ''
    })
    setEditLineQtys(initQtys)
    setEditLineNotes(initNotes)

    try {
      if (r.kind === 'PO') {
        const hist = await getReceiptHistory(r.id)
        setDocHistory(hist)
      } else {
        const hist = await getStockInboundHistory(r.id)
        setDocHistory(hist)
      }
    } catch {
      setDocHistory([])
    }
  }, [])

  // Execute receive PO
  const handleSaveReceive = async () => {
    if (!receiveModalPo) return
    const linesWithQty = receiveLinesState
      .map((st) => ({
        lineId: st.lineId,
        materialId: st.materialId,
        qty: Number(st.qtyToReceive) || 0,
        note: st.lineNote,
        shortAction: st.shortAction,
      }))
      .filter((x) => x.qty > 0)

    const linesWithShort = receiveLinesState.filter(
      (st) => Boolean(st.shortAction) && (Number(st.qtyToReceive) || 0) === 0,
    )

    if (linesWithQty.length === 0 && linesWithShort.length === 0) {
      showToast('Chưa nhập số lượng nhận ở dòng nào')
      return
    }

    try {
      if (linesWithQty.length > 0) {
        if (receiveModalPo.kind === 'FREE') {
          await receiveFreePo(receiveModalPo.id, {
            note: receiveNote.trim() || undefined,
            lines: linesWithQty.map((x) => ({ po_free_line_id: x.lineId, qty: x.qty })),
          })
        } else {
          await receivePurchaseOrder(receiveModalPo.id, {
            note: receiveNote.trim() || undefined,
            deliverer_name: receiveDelivererName.trim() || undefined,
            lines: linesWithQty.map((x) => ({
              po_line_id: x.lineId,
              qty: x.qty,
              notes: x.note.trim() || undefined,
            })),
          })
        }
      }

      if (receiveModalPo.kind === 'FREE') {
        showToast(`Đã nhận hàng thành công cho PO ${receiveModalPo.code}`)
        setReceiveModalPo(null)
        await loadAllData()
        return
      }

      for (const sh of linesWithShort) {
        const poLine = receiveModalPo.lines?.find((l) => l.id === sh.lineId)
        const orderQty = Number(poLine?.order_qty || 0)
        const received = Number(poLine?.received_qty || 0)
        const shortQty = Math.max(0, orderQty - received)
        if (shortQty > 0) {
          const action = sh.shortAction === 'Chấp nhận thiếu' ? 'accept' : sh.shortAction === 'Hủy phần còn lại' ? 'cancel_rest' : sh.shortAction === 'Khiếu nại NCC' ? 'claim' : 'wait'
          await recordLineShort(receiveModalPo.id, sh.lineId, {
            short_qty: shortQty,
            action,
            note: `Xử lý thiếu: ${sh.shortAction}`,
          })
        }
      }

      showToast(`Đã nhận hàng thành công cho PO ${receiveModalPo.code}`)
      setReceiveModalPo(null)
      await loadAllData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi lưu phiếu nhận hàng')
    }
  }

  // Execute doc edit
  const handleSaveDocEdit = async () => {
    if (!detailDoc) return
    const reason = editReason.trim()
    if (!reason) {
      showToast('Bắt buộc nhập lý do chỉnh sửa')
      return
    }

    try {
      if (detailDoc.kind === 'PO') {
        const patchLines = detailDoc.lines.map((l) => ({
          id: l.id,
          qty: editLineQtys[l.id] ?? l.qty,
          note: editLineNotes[l.id] ?? l.note,
        }))
        await patchReceipt(detailDoc.id, { lines: patchLines }, reason)
      } else {
        const patchLines = detailDoc.lines.map((l) => ({
          id: l.id,
          qty_base: editLineQtys[l.id] ?? l.qty,
        }))
        await patchStockInbound(detailDoc.id, { lines: patchLines, note: reason })
      }
      showToast(`Đã cập nhật phiếu ${detailDoc.code}`)
      setDetailDoc(null)
      await loadAllData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi cập nhật phiếu')
    }
  }

  // Execute doc void
  const handleVoidDoc = async () => {
    if (!detailDoc) return
    const reason = window.prompt(`Lý do hủy phiếu ${detailDoc.code} (bắt buộc):`)
    if (reason === null) return
    if (!reason.trim()) {
      showToast('Bắt buộc nhập lý do hủy')
      return
    }

    try {
      if (detailDoc.kind === 'PO') {
        await voidReceipt(detailDoc.id, reason.trim())
      } else {
        await voidStockInbound(detailDoc.id, reason.trim())
      }
      showToast(`Đã hủy phiếu ${detailDoc.code}`)
      setDetailDoc(null)
      await loadAllData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi hủy phiếu')
    }
  }

  // Execute direct inbound
  const handleSaveDirectInbound = async () => {
    const validLines = directLines
      .filter((l) => l.materialId && Number(l.qty) > 0)
      .map((l) => ({
        material_id: l.materialId,
        qty_base: Number(l.qty),
        note: l.note.trim() || null,
      }))

    if (validLines.length === 0) {
      showToast('Chưa có dòng hợp lệ (cần chọn vật tư và số lượng > 0)')
      return
    }

    try {
      const created = await createStockInbound({
        source: directSource,
        deliverer_name: directDelivererName.trim() || null,
        note: directNote.trim() || null,
        lines: validLines,
      })
      showToast(`Đã lưu phiếu nhập trực tiếp ${created.code} (${validLines.length} dòng)`)
      setDirectModalOpen(false)
      await loadAllData()
      const createdRow: UnifiedReceiptRow = {
        id: created.id,
        code: created.code,
        kind: 'DIRECT',
        sourceType: 'Trực tiếp',
        ref: directSource,
        supplierName: '—',
        materialNames: validLines.map(vl => materialMap.get(vl.material_id)?.material_name || 'Vật tư').join(', '),
        lineCount: validLines.length,
        at: created.created_at,
        by: created.created_by,
        delivererName: created.deliverer_name || directDelivererName.trim() || '',
        editCount: 0,
        voidReason: '',
        note: created.note || '',
        status: 'Hoạt động',
        isVoid: false,
        lines: (created.lines || validLines).map((l: any) => ({
          id: l.id || '',
          materialId: l.material_id,
          materialName: materialMap.get(l.material_id)?.material_name || 'Vật tư',
          groupCode: materialMap.get(l.material_id)?.group_code,
          uom: materialMap.get(l.material_id)?.base_uom || '',
          qty: l.qty_base,
          note: l.note,
        })),
      }
      handleOpenPrintReceipt(createdRow)
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi tạo phiếu nhập trực tiếp')
    }
  }

  // Fill all remaining in receive modal
  const handleFillAllRemaining = () => {
    if (!receiveModalPo) return
    setReceiveLinesState((prev) =>
      prev.map((st) => {
        const poLine = receiveModalPo.lines?.find((l) => l.id === st.lineId)
        if (!poLine || isPoLineClosed(poLine)) return st
        const rem = Math.max(0, Number(poLine.order_qty || 0) - Number(poLine.received_qty || 0))
        return { ...st, qtyToReceive: rem }
      }),
    )
  }

  const handleClearAllQtys = () => {
    setReceiveLinesState((prev) => prev.map((st) => ({ ...st, qtyToReceive: '' })))
  }

  // Receive modal columns with group attributes
  const receiveModalCols = useMemo(() => {
    if (!receiveModalPo) return []
    const firstLineMatId = receiveModalPo.lines?.[0]?.material_id
    const mat = firstLineMatId ? materialMap.get(firstLineMatId) : null
    const groupDef = getGroupDef(mat?.group_code)

    const cols: ExcelColumnDef<any>[] = [
      {
        key: 'code',
        header: 'Mã',
        width: 100,
        getValue: (l) => {
          const m = materialMap.get(l.material_id)
          return m?.code || l.material_code || ''
        },
      },
      {
        key: 'name',
        header: 'Tên vật tư',
        width: 200,
        getValue: (l) => {
          const m = materialMap.get(l.material_id)
          return m?.name || m?.material_name || l.material_name || 'Vật tư'
        },
      },
    ]

    // Quy cách và Màu lấy từ dòng PO (nếu có), nếu không thì từ thuộc tính vật tư; để đối chiếu hàng thực nhận.
    const lineSpecColor = (l: any) => {
      const m = materialMap.get(l.material_id)
      const ma = (m?.attributes ?? {}) as Record<string, unknown>
      return poLineSpecColor(l, ma)
    }
    cols.push(
      { key: 'spec_line', header: 'Quy cách', width: 130, getValue: (l) => lineSpecColor(l).spec || '—' },
      { key: 'color_line', header: 'Màu', width: 80, getValue: (l) => lineSpecColor(l).color || '—' },
    )

    // Group specific attributes (bỏ các thuộc tính đã hiện ở cột Quy cách / Màu)
    for (const attr of groupDef.attributes.filter(
      (x) =>
        !['spec', 'quy_cach', 'color', 'mau'].includes(x.key) &&
        // chỉ hiện cột thuộc tính khi có ít nhất một dòng có dữ liệu (tránh cột toàn dấu —)
        receiveModalPo.lines?.some((l) => {
          const v = (materialMap.get(l.material_id)?.attributes as any)?.[x.key]
          return v != null && String(v).trim() !== ''
        }),
    )) {
      cols.push({
        key: `attr_${attr.key}`,
        header: attr.label,
        width: attr.width,
        getValue: (l) => {
          const m = materialMap.get(l.material_id)
          return (m?.attributes as any)?.[attr.key] ?? '—'
        },
      })
    }

    cols.push(
      {
        key: 'uom',
        header: 'ĐVT',
        width: 60,
        align: 'center',
        getValue: (l) => {
          const m = materialMap.get(l.material_id)
          return m?.base_uom || m?.uom_default || l.order_uom || ''
        },
      },
      {
        key: 'order_qty',
        header: 'Đặt (PO)',
        width: 80,
        isNumeric: true,
        tooltip: () => 'Số đặt do Mua hàng quyết định, Kho không sửa',
        getValue: (l) => Number(l.order_qty || 0),
        renderCell: (l) => fmtN(l.order_qty),
      },
      {
        key: 'received_qty',
        header: 'Đã nhận',
        width: 80,
        isNumeric: true,
        getValue: (l) => Number(l.received_qty || 0),
        renderCell: (l) => fmtN(l.received_qty),
      },
      {
        key: 'rem',
        header: 'Còn thiếu',
        width: 80,
        isNumeric: true,
        getValue: (l) => Math.max(0, Number(l.order_qty || 0) - Number(l.received_qty || 0)),
        renderCell: (l) => {
          const rem = Math.max(0, Number(l.order_qty || 0) - Number(l.received_qty || 0))
          return <b>{fmtN(rem)}</b>
        },
      },
      {
        key: 'qty_receive',
        header: 'Nhận đợt này',
        width: 110,
        isNumeric: true,
        getValue: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          return st?.qtyToReceive ?? ''
        },
        renderCell: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          const closed = isPoLineClosed(l)
          const rem = Math.max(0, Number(l.order_qty || 0) - Number(l.received_qty || 0))
          return (
            <input
              type="number"
              min={0}
              max={rem}
              disabled={!canWriteInventory || closed}
              value={st?.qtyToReceive ?? ''}
              onChange={(e) => {
                const val = e.target.value
                const num = Number(val)
                const clamped = num > rem ? rem : val === '' ? '' : Math.max(0, num)
                setReceiveLinesState((prev) =>
                  prev.map((item) => (item.lineId === l.id ? { ...item, qtyToReceive: clamped } : item)),
                )
              }}
              style={{
                width: '100%',
                border: '1px solid #999',
                padding: '2px 4px',
                textAlign: 'right',
                background: closed ? '#f4f4f4' : '#fff',
              }}
            />
          )
        },
      },
      {
        key: 'line_note',
        header: 'Ghi chú dòng',
        width: 150,
        getValue: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          return st?.lineNote ?? ''
        },
        renderCell: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          return (
            <input
              type="text"
              disabled={!canWriteInventory}
              value={st?.lineNote ?? ''}
              onChange={(e) => {
                const val = e.target.value
                setReceiveLinesState((prev) =>
                  prev.map((item) => (item.lineId === l.id ? { ...item, lineNote: val } : item)),
                )
              }}
              style={{ width: '100%', border: '1px solid #999', padding: '2px 4px' }}
            />
          )
        },
      },
      {
        key: 'short_res',
        header: 'Xử lý phần thiếu',
        width: 150,
        getValue: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          return st?.shortAction ?? ''
        },
        renderCell: (l) => {
          const st = receiveLinesState.find((x) => x.lineId === l.id)
          return (
            <select
              disabled={!canWriteInventory}
              value={st?.shortAction ?? ''}
              onChange={(e) => {
                const val = e.target.value as ShortResOption | ''
                setReceiveLinesState((prev) =>
                  prev.map((item) => (item.lineId === l.id ? { ...item, shortAction: val } : item)),
                )
              }}
              style={{ width: '100%', border: '1px solid #999', padding: '2px 4px', background: '#fff' }}
            >
              <option value="">—</option>
              <option value="Chờ giao thêm">Chờ giao thêm</option>
              <option value="Chấp nhận thiếu">Chấp nhận thiếu</option>
              <option value="Hủy phần còn lại">Hủy phần còn lại</option>
              <option value="Khiếu nại NCC">Khiếu nại NCC</option>
            </select>
          )
        },
      },
    )

    return cols
  }, [receiveModalPo, materialMap, receiveLinesState, isPoLineClosed, canWriteInventory])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', padding: '8px 12px', background: '#fff', boxSizing: 'border-box' }}>
      <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Nhập kho' }]} />
      <PageHeader title="Nhập kho" subtitle="Tiếp nhận vật tư và theo dõi phiếu nhập" />
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            right: 14,
            bottom: 14,
            background: '#222',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 3,
            zIndex: 9999,
            fontSize: 13,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* Sub Tabs */}
      <div style={{ display: 'flex', gap: 6, borderBottom: '2px solid #ddd', marginBottom: 8 }}>
        <button
          onClick={() => setActiveSubTab('po')}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderBottom: activeSubTab === 'po' ? '3px solid #1f6feb' : '3px solid transparent',
            background: 'none',
            fontWeight: activeSubTab === 'po' ? 700 : 500,
            color: activeSubTab === 'po' ? '#1f6feb' : '#444',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          PO chờ nhận{' '}
          {pendingPoCount > 0 && (
            <span
              style={{
                background: '#e0eaff',
                color: '#0b4fb3',
                fontSize: 11,
                padding: '1px 6px',
                borderRadius: 10,
                marginLeft: 4,
              }}
            >
              {pendingPoCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveSubTab('doc')}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderBottom: activeSubTab === 'doc' ? '3px solid #1f6feb' : '3px solid transparent',
            background: 'none',
            fontWeight: activeSubTab === 'doc' ? 700 : 500,
            color: activeSubTab === 'doc' ? '#1f6feb' : '#444',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Phiếu nhập{' '}
          <span
            style={{
              background: '#eee',
              color: '#555',
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 10,
              marginLeft: 4,
            }}
          >
            {unifiedReceiptRows.length}
          </span>
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Tìm trong bảng…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 220, padding: '4px 8px', border: '1px solid #aaa', borderRadius: 2 }}
        />

        <div style={{ flex: 1 }} />

        <button
          onClick={() => {
            setDirectDelivererName('')
            setDirectNote('')
            setDirectSource('MISC_PURCHASE')
            setDirectLines([
              { materialId: '', qty: '', note: '' },
              { materialId: '', qty: '', note: '' },
              { materialId: '', qty: '', note: '' },
            ])
            setDirectModalOpen(true)
          }}
          disabled={!canWriteInventory}
          style={{
            padding: '5px 12px',
            background: canWriteInventory ? '#f6f8fa' : '#eee',
            border: '1px solid #bbb',
            borderRadius: 3,
            cursor: canWriteInventory ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            color: canWriteInventory ? '#222' : '#888',
          }}
        >
          ＋ Nhập trực tiếp
        </button>

        <button
          onClick={() => setSearchQuery('')}
          disabled={!searchQuery}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: searchQuery ? 'pointer' : 'default',
            color: searchQuery ? '#333' : '#999',
          }}
        >
          ✕ Bỏ lọc
        </button>

        <button
          onClick={() => setResetWidthsCounter((c) => c + 1)}
          title="Đặt lại độ rộng các cột về mặc định"
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ↔ Đặt lại độ rộng
        </button>

        <button
          onClick={() => {
            if (activeSubTab === 'po') {
              const headers = poCols.map((c) => getColHeader(c))
              const rows = receivablePos.map((r) => poCols.map((c) => getColVal(c, r)))
              exportToCsv('po-cho-nhan.csv', headers, rows)
            } else {
              const headers = recCols.map((c) => getColHeader(c))
              const rows = unifiedReceiptRows.map((r) => recCols.map((c) => getColVal(c, r)))
              exportToCsv('phieu-nhap.csv', headers, rows)
            }
            showToast('Đã xuất file CSV (mở được bằng Excel)')
          }}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ⬇ Excel
        </button>
      </div>

      {/* Grid Container */}
      <div style={{ flex: 1, minHeight: 0, border: '1px solid #ccc' }}>
        {activeSubTab === 'po' ? (
          <ExcelGrid<PurchaseOrderDto>
            id="po"
            columns={poCols}
            data={receivablePos}
            searchQuery={searchQuery}
            freeze={2}
            resetWidthsTrigger={resetWidthsCounter}
            emptyMessage={loading ? 'Đang tải danh sách PO chờ nhận…' : 'Chưa có đơn mua hàng nào'}
            onCellClick={(p, col) => {
              if (getColKey(col) === 'code') {
                handleOpenReceive(p)
              }
            }}
          />
        ) : (
          <ExcelGrid<UnifiedReceiptRow>
            id="rec"
            columns={recCols}
            data={unifiedReceiptRows}
            searchQuery={searchQuery}
            freeze={2}
            resetWidthsTrigger={resetWidthsCounter}
            rowCls={(r: UnifiedReceiptRow) => (r.isVoid ? 'void' : undefined)}
            emptyMessage={loading ? 'Đang tải danh sách phiếu nhập…' : 'Chưa có phiếu nhập nào'}
            onCellClick={(r, col) => {
              if (getColKey(col) === 'code') {
                void handleOpenDocModal(r)
              }
            }}
          />
        )}
      </div>

      {/* MODAL: Nhận hàng theo PO */}
      {receiveModalPo && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(1180px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Nhận hàng {receiveModalPo.code}</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setReceiveModalPo(null)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Chips info */}
              <div style={{ display: 'flex', gap: 16, padding: '6px 10px', background: '#fafafa', border: '1px solid #ddd', fontSize: 13, flexWrap: 'wrap' }}>
                <span>Nhà cung cấp <b>{receiveModalPo.supplier_name || '—'}</b></span>
                <span>
                  Nhóm hàng{' '}
                  <b>
                    {getGroupDef(
                      materialMap.get(receiveModalPo.lines?.[0]?.material_id || '')?.group_code,
                    ).name}
                  </b>
                  {' '}(cột thông số theo format của NCC này)
                </span>
                <span>
                  ETA <b>{formatDateFull((receiveModalPo as any).expected_delivery_date || (receiveModalPo as any).eta || '')}</b>
                </span>
                <span style={{ color: '#888' }}>Kho chỉ nhập số nhận; số đặt và PO do Mua hàng chỉnh</span>
              </div>

              {/* Table of lines */}
              <div style={{ border: '1px solid #bbb', maxHeight: '50vh', overflowY: 'auto' }}>
                <ExcelGrid
                  columns={receiveModalCols}
                  data={receiveModalPo.lines || []}
                  freeze={1}
                />
              </div>

              {/* Toolbar */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', border: '1px solid #ddd', background: '#fdfdfd' }}>
                <button
                  onClick={handleFillAllRemaining}
                  disabled={!canWriteInventory}
                  style={{ padding: '4px 10px', background: '#f6f8fa', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                >
                  Nhận hết phần còn lại
                </button>
                <button
                  onClick={handleClearAllQtys}
                  disabled={!canWriteInventory}
                  style={{ padding: '4px 10px', background: '#f6f8fa', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                >
                  Xóa SL nhập
                </button>
                <span style={{ color: '#888', fontSize: 12 }}>
                  Có thể dán cột số lượng từ Excel vào ô “Nhận đợt này”
                </span>
              </div>

              {/* Deliverer and Note */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>
                    Người giao hàng
                  </label>
                  <input
                    type="text"
                    placeholder="Họ tên người giao hàng (tùy chọn)..."
                    value={receiveDelivererName}
                    onChange={(e) => setReceiveDelivererName(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Ghi chú phiếu</label>
                  <input
                    type="text"
                    placeholder="VD: NCC giao đợt 2, thiếu 1 dòng…"
                    value={receiveNote}
                    onChange={(e) => setReceiveNote(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setReceiveModalPo(null)}
                style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
              >
                Hủy
              </button>
              <button
                onClick={() => void handleSaveReceive()}
                disabled={!canWriteInventory}
                style={{
                  padding: '5px 14px',
                  background: canWriteInventory ? '#1f6feb' : '#999',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  cursor: canWriteInventory ? 'pointer' : 'not-allowed',
                }}
              >
                Lưu phiếu nhập
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Xem / Sửa phiếu nhập */}
      {detailDoc && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(980px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                {detailDoc.sourceType === 'PO' ? 'Phiếu nhập PO ' : 'Phiếu nhập trực tiếp '}
                {detailDoc.code}
              </span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setDetailDoc(null)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Chips */}
              <div style={{ display: 'flex', gap: 16, padding: '6px 10px', background: '#fafafa', border: '1px solid #ddd', fontSize: 13, flexWrap: 'wrap' }}>
                <span>Phiếu <b>{detailDoc.code}</b></span>
                <span>Nguồn <b>{detailDoc.ref}</b></span>
                {detailDoc.supplierName !== '—' && <span>NCC <b>{detailDoc.supplierName}</b></span>}
                <span>Người giao <b>{detailDoc.delivererName || '—'}</b></span>
                <span>{formatDateTimeFull(detailDoc.at)} · {detailDoc.by}</span>
                <span style={{ color: detailDoc.isVoid ? '#999' : '#1b6e2d', fontWeight: 600 }}>{detailDoc.status}</span>
              </div>

              {/* Lines Table */}
              <div style={{ border: '1px solid #bbb', maxHeight: '40vh', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f0f0f0', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40, textAlign: 'center' }}>STT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left' }}>Tên vật tư</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 130 }}>Nhóm</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'center', width: 60 }}>ĐVT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 100 }}>SL nhận</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 90 }}>SL đã ghi</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 220 }}>Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailDoc.lines.map((l, i) => (
                      <tr key={l.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f9f9f9', border: '1px solid #ddd' }}>{i + 1}</td>
                        <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{l.materialName}{detailDoc.kind === 'PO' && <div className="text-xs text-slate-600">{[l.spec, l.color].filter(Boolean).join(' · ') || '—'}</div>}</td>
                        <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{getGroupDef(l.groupCode).name}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>{l.uom}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                          {isEditingDoc ? (
                            <input
                              type="number"
                              min={0.01}
                              step="any"
                              value={editLineQtys[l.id] ?? l.qty}
                              onChange={(e) => {
                                const val = Number(e.target.value)
                                setEditLineQtys((prev) => ({ ...prev, [l.id]: val }))
                              }}
                              style={{ width: '100%', textAlign: 'right', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          ) : (
                            fmtN(l.qty)
                          )}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd', color: '#666' }}>{fmtN(l.qty)}</td>
                        <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                          {isEditingDoc ? (
                            <input
                              type="text"
                              value={editLineNotes[l.id] ?? (l.note || '')}
                              onChange={(e) => {
                                const val = e.target.value
                                setEditLineNotes((prev) => ({ ...prev, [l.id]: val }))
                              }}
                              style={{ width: '100%', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          ) : (
                            l.note || '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Editing reason */}
              {isEditingDoc && (
                <div>
                  <label style={{ fontSize: 12, color: '#b00020', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                    Lý do chỉnh sửa *
                  </label>
                  <input
                    type="text"
                    placeholder="VD: Đếm lại thực tế, nhập sai số"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    style={{ width: '100%', border: '1px solid #c00', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {/* Change history */}
              <div>
                <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 4, fontWeight: 600 }}>
                  Lịch sử thay đổi
                </label>
                <div style={{ border: '1px solid #ddd', maxHeight: 140, overflowY: 'auto', fontSize: 12, padding: '4px 8px', background: '#fafafa' }}>
                  {docHistory.length === 0 ? (
                    <div style={{ color: '#888', padding: 4 }}>Chưa có thay đổi nào được ghi lại</div>
                  ) : (
                    docHistory.map((h, i) => (
                      <div key={i} style={{ padding: '4px 0', borderBottom: '1px dotted #ccc' }}>
                        <b>{h.action === 'PRINT' ? 'Đã in phiếu' : h.action === 'VOID' ? 'Đã hủy phiếu' : (h.action || 'Thay đổi')}</b> · {h.by} · {formatDateTimeFull(h.at)}
                        {h.reason && <div style={{ color: '#666' }}>{h.reason}</div>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {isEditingDoc ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditingDoc(false)
                      setEditReason('')
                    }}
                    style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                  >
                    ✕ Bỏ sửa
                  </button>
                  <button
                    onClick={() => void handleSaveDocEdit()}
                    style={{ padding: '5px 14px', background: '#1f6feb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                  >
                    ✓ Xác nhận sửa
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setDetailDoc(null)}
                    style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                  >
                    Đóng
                  </button>
                  <button
                    onClick={() => handleOpenPrintReceipt(detailDoc)}
                    style={{
                      padding: '5px 14px',
                      background: '#1f6feb',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Printer size={14} aria-hidden="true" /> In phiếu
                  </button>
                  {canWriteInventory && !detailDoc.isVoid && (
                    <>
                      <button
                        onClick={() => setIsEditingDoc(true)}
                        style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                      >
                        ✎ Sửa phiếu
                      </button>
                      <button
                        onClick={() => void handleVoidDoc()}
                        style={{ padding: '5px 14px', background: '#fff', border: '1px solid #d32f2f', color: '#d32f2f', cursor: 'pointer' }}
                      >
                        Hủy phiếu
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nhập trực tiếp */}
      {directModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(900px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Nhập trực tiếp</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setDirectModalOpen(false)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Nguồn nhập *</label>
                  <select
                    value={directSource}
                    onChange={(e) => setDirectSource(e.target.value as any)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', background: '#fff' }}
                  >
                    <option value="MISC_PURCHASE">Mua lẻ</option>
                    <option value="OPENING">Đầu kỳ</option>
                    <option value="OUTSIDE_PO">Ngoài PO</option>
                    <option value="OTHER">Khác</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Người giao hàng</label>
                  <input
                    type="text"
                    placeholder="Họ tên người giao (tùy chọn)..."
                    value={directDelivererName}
                    onChange={(e) => setDirectDelivererName(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Ghi chú phiếu</label>
                  <input
                    type="text"
                    placeholder="VD: Mua ngoài chợ Kim Biên…"
                    value={directNote}
                    onChange={(e) => setDirectNote(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Lines Table */}
              <div
                onPaste={handlePasteDirectLines}
                style={{ border: '1px solid #bbb', maxHeight: '42vh', overflow: 'auto' }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f0f0f0', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40, textAlign: 'center' }}>STT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', minWidth: 260 }}>Vật tư * (có thể dán từ Excel)</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'center', width: 70 }}>ĐVT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 100 }}>Tồn hiện tại</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 110 }}>SL nhập *</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 200 }}>Ghi chú</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {directLines.map((l, i) => {
                      const selMat = materialMap.get(l.materialId)
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f9f9f9', border: '1px solid #ddd' }}>{i + 1}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                            <MaterialTypeahead
                              materials={materials}
                              value={l.materialId}
                              onChange={(val) => {
                                setDirectLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, materialId: val, error: undefined } : item)),
                                )
                              }}
                              placeholder="Gõ tên hoặc thông số để chọn…"
                              error={l.error}
                            />
                            {l.error && (
                              <div style={{ fontSize: 11, color: '#c00', marginTop: 2, fontWeight: 500 }}>
                                {l.error}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>
                            {selMat?.base_uom || selMat?.uom_default || '—'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                            {selMat ? fmtN(selMat.physical_available ?? selMat.available) : '—'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                            <input
                              type="number"
                              min={0.01}
                              step="any"
                              value={l.qty}
                              onChange={(e) => {
                                const val = e.target.value
                                setDirectLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, qty: val } : item)),
                                )
                              }}
                              style={{ width: '100%', textAlign: 'right', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                            <input
                              type="text"
                              value={l.note}
                              onChange={(e) => {
                                const val = e.target.value
                                setDirectLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, note: val } : item)),
                                )
                              }}
                              style={{ width: '100%', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>
                            <button
                              onClick={() => setDirectLines((prev) => prev.filter((_, idx) => idx !== i))}
                              style={{ border: 'none', background: 'none', color: '#b00020', cursor: 'pointer', fontWeight: 700 }}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Add line button */}
              <div>
                <button
                  onClick={() => setDirectLines((prev) => [...prev, { materialId: '', qty: '', note: '' }])}
                  style={{ padding: '4px 10px', background: '#f6f8fa', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                >
                  ＋ Thêm dòng
                </button>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setDirectModalOpen(false)}
                style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
              >
                Hủy
              </button>
              <button
                onClick={() => void handleSaveDirectInbound()}
                style={{ padding: '5px 14px', background: '#1f6feb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              >
                Lưu phiếu nhập
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: In phiếu nhập kho */}
      <WarehouseDocumentPrintModal
        isOpen={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        data={printDocData}
        onPrint={() => {
          if (printDocMeta) {
            const printPromise =
              printDocMeta.kind === 'PO'
                ? recordReceiptPrintLog(printDocMeta.id)
                : recordStockInboundPrintLog(printDocMeta.id)

            void printPromise
              .then(() => {
                if (detailDoc?.id === printDocMeta.id) {
                  const histPromise =
                    printDocMeta.kind === 'PO'
                      ? getReceiptHistory(printDocMeta.id)
                      : getStockInboundHistory(printDocMeta.id)
                  histPromise
                    .then((h) => {
                      if (h) setDocHistory(h)
                    })
                    .catch(() => {})
                }
              })
              .catch(() => {})
          }
        }}
      />
    </div>
  )
}
